import { ButtonHTMLAttributes, ForwardedRef, ReactNode } from 'react';

import { css, cx, Styles } from '@flipster/styled-system/css';
import { iconButton, IconButtonVariantProps } from '@flipster/styled-system/recipes';
import { ColorToken } from '@flipster/styled-system/tokens';

import { IsomorphicLink, IsomorphicLinkProps } from '../design-utils/isomorphic-link';

interface BaseProps {
  hierarchy?: IconButtonVariantProps['hierarchy'];
  size?: IconButtonVariantProps['size'];
  icon?: ReactNode;
  color?: ColorToken;
  css?: Styles;
}

type ButtonElementProps = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps | 'className' | 'children'> & {
    ref?: ForwardedRef<HTMLButtonElement>;
  };
type LinkElementProps = BaseProps &
  Omit<IsomorphicLinkProps, keyof BaseProps | 'className' | 'children'> & {
    ref?: ForwardedRef<HTMLAnchorElement>;
  };
export type IconButtonProps<T extends 'button' | 'a' | undefined = 'button'> = { as?: T } & (T extends 'button'
  ? ButtonElementProps
  : LinkElementProps);

export const ButtonComponent = ({ hierarchy, size, icon, color, css: cssProps, ref, ...props }: ButtonElementProps) => {
  const classes = iconButton({ hierarchy, size });
  return (
    <button ref={ref} className={cx(classes.root, css({ color, ...cssProps }))} {...props}>
      {icon}
    </button>
  );
};
const LinkComponent = ({ hierarchy, size, ref, icon, color, css: cssProps, ...htmlProps }: LinkElementProps) => {
  const classes = iconButton({ hierarchy, size });

  return (
    <IsomorphicLink ref={ref} className={cx(classes.root, css({ color, ...cssProps }))} {...htmlProps}>
      {icon}
    </IsomorphicLink>
  );
};

export const IconButton = <T extends 'button' | 'a' | undefined = 'button'>({
  as = 'button',
  ...htmlProps
}: IconButtonProps<T>) => {
  if (as === 'button') {
    return <ButtonComponent {...(htmlProps as ButtonElementProps)} />;
  }
  if (as === 'a') {
    return <LinkComponent {...(htmlProps as LinkElementProps)} />;
  }
  return null;
};
